import AppLayout from "@/components/layout/AppLayout";
import { api } from "@/api/http";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Search, RefreshCcw, Plus, Pencil, ArrowRightLeft, Package, AlertTriangle } from "lucide-react";

type Peca = {
  id: string;
  codigo?: string | null;
  descricao: string;
  marca?: string | null;
  unidade?: string | null;
  ncm?: string | null;
  custo?: any;
  precoVenda?: any;
  estoqueAtual?: any;
  estoqueMin?: any;
  createdAt?: string;
};

type Meta = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  q: string;
  lowStock?: boolean;
};

function money(v: any) {
  const n = Number(v ?? 0);
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function qty(v: any) {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n.toLocaleString("pt-BR") : "0";
}

export default function PecasList() {
  const [searchParams, setSearchParams] = useSearchParams();

  const qParam = searchParams.get("q") ?? "";
  const pageParam = Number(searchParams.get("page") ?? "1");
  const limitParam = Number(searchParams.get("limit") ?? "20");
  const lowStockParam = searchParams.get("lowStock") === "true";

  const [q, setQ] = useState(qParam);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<Peca[]>([]);
  const [meta, setMeta] = useState<Meta>({
    page: pageParam,
    limit: limitParam,
    total: 0,
    totalPages: 1,
    q: qParam,
    lowStock: lowStockParam,
  });

  const canPrev = useMemo(() => meta.page > 1, [meta.page]);
  const canNext = useMemo(() => meta.page < meta.totalPages, [meta.page, meta.totalPages]);

  function updateUrl(next: { q?: string; page?: number; limit?: number; lowStock?: boolean }) {
    const p = new URLSearchParams(searchParams);

    if (next.q !== undefined) {
      if (next.q.trim()) p.set("q", next.q.trim());
      else p.delete("q");
    }
    if (next.page !== undefined) p.set("page", String(next.page));
    if (next.limit !== undefined) p.set("limit", String(next.limit));

    if (next.lowStock !== undefined) {
      if (next.lowStock) p.set("lowStock", "true");
      else p.delete("lowStock");
    }

    if (p.get("page") === "1") p.delete("page");
    if (p.get("limit") === "20") p.delete("limit");

    setSearchParams(p);
  }

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/api/pecas", {
        params: {
          q: qParam || undefined,
          page: pageParam || 1,
          limit: limitParam || 20,
          lowStock: lowStockParam ? "true" : undefined,
        },
      });

      setRows(data?.data ?? []);
      setMeta(data?.meta ?? { page: 1, limit: 20, total: 0, totalPages: 1, q: "", lowStock: false });
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || "Erro ao carregar peças";
      toast.error(msg);
      setRows([]);
      setMeta((m) => ({ ...m, total: 0, totalPages: 1 }));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qParam, pageParam, limitParam, lowStockParam]);

  function onSearch() {
    updateUrl({ q, page: 1 });
  }

  function isLowStock(p: Peca) {
    const atual = Number(p.estoqueAtual ?? 0);
    const min = Number(p.estoqueMin ?? 0);
    return min > 0 && atual <= min;
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* HEADER */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Package size={20} />
              Peças
            </h1>
            <p className="text-sm text-slate-500">
              {meta.total ? `${meta.total} peça(s)` : "Catálogo e estoque"}
              {lowStockParam ? " • abaixo do mínimo" : ""}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={load} className="gap-2" disabled={loading}>
              <RefreshCcw size={16} />
              Atualizar
            </Button>

            <Link to="/pecas/nova">
              <Button className="gap-2">
                <Plus size={16} />
                Nova Peça
              </Button>
            </Link>
          </div>
        </div>

        {/* BUSCA + FILTRO */}
        <div className="bg-white p-4 rounded-xl shadow-sm">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 bg-slate-100 px-3 py-2 rounded-lg flex-1 min-w-[260px]">
              <Search size={16} className="text-slate-500" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onSearch()}
                placeholder="Buscar por código, descrição, marca, NCM..."
                className="bg-transparent outline-none text-sm w-full"
              />
            </div>

            <Button onClick={onSearch} disabled={loading} className="whitespace-nowrap">
              Buscar
            </Button>

            <Button
              type="button"
              variant={lowStockParam ? "default" : "outline"}
              className="gap-2 whitespace-nowrap"
              onClick={() => updateUrl({ lowStock: !lowStockParam, page: 1 })}
            >
              <AlertTriangle size={16} />
              Abaixo do mínimo
            </Button>
          </div>

          <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setQ("");
                updateUrl({ q: "", lowStock: false, page: 1 });
              }}
            >
              Limpar filtros
            </Button>

            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Por página:</span>
              <select
                value={limitParam}
                onChange={(e) => updateUrl({ limit: Number(e.target.value), page: 1 })}
                className="h-9 rounded-md border border-slate-300 px-2 text-sm bg-white"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>

        {/* TABELA */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3">Código</th>
                  <th className="text-left px-4 py-3">Descrição</th>
                  <th className="text-left px-4 py-3">Un</th>
                  <th className="text-left px-4 py-3">Custo</th>
                  <th className="text-left px-4 py-3">Venda</th>
                  <th className="text-left px-4 py-3">Estoque</th>
                  <th className="text-right px-4 py-3">Ações</th>
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                      Carregando peças...
                    </td>
                  </tr>
                )}

                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                      Nenhuma peça encontrada.
                    </td>
                  </tr>
                )}

                {!loading &&
                  rows.map((p) => (
                    <tr
                      key={p.id}
                      className={`border-t hover:bg-slate-50 ${isLowStock(p) ? "bg-amber-50/40" : ""}`}
                    >
                      <td className="px-4 py-3 font-medium">{p.codigo || "-"}</td>

                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{p.descricao}</div>
                        <div className="text-xs text-slate-500">
                          {p.marca ? `Marca: ${p.marca}` : ""}
                          {p.ncm ? ` • NCM: ${p.ncm}` : ""}
                        </div>
                      </td>

                      <td className="px-4 py-3">{p.unidade || "un"}</td>
                      <td className="px-4 py-3">{money(p.custo)}</td>
                      <td className="px-4 py-3">{money(p.precoVenda)}</td>

                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{qty(p.estoqueAtual)}</div>
                        <div className="text-xs text-slate-500">
                          Mín: {qty(p.estoqueMin)} {isLowStock(p) ? "• baixo" : ""}
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <Link to={`/pecas/${p.id}/editar`}>
                            <Button size="sm" variant="outline" className="gap-2">
                              <Pencil size={16} />
                              Editar
                            </Button>
                          </Link>
                          <Link to={`/pecas/${p.id}/historico`}>
                            <Button variant="outline">Histórico</Button>
                          </Link>



                          <Link to={`/estoque/movimentar?pecaId=${p.id}`}>
                            <Button size="sm" variant="outline" className="gap-2">
                              <ArrowRightLeft size={16} />
                              Movimentar
                            </Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* PAGINAÇÃO */}
          <div className="flex items-center justify-between px-4 py-3 border-t bg-white">
            <p className="text-xs text-slate-500">
              Página <span className="font-medium text-slate-800">{meta.page}</span> de{" "}
              <span className="font-medium text-slate-800">{meta.totalPages}</span>
            </p>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!canPrev || loading}
                onClick={() => updateUrl({ page: meta.page - 1 })}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!canNext || loading}
                onClick={() => updateUrl({ page: meta.page + 1 })}
              >
                Próxima
              </Button>
            </div>
          </div>
        </div>

        <div className="text-xs text-slate-500">
          Linhas destacadas indicam estoque no mínimo (ou abaixo).
        </div>
      </div>
    </AppLayout>
  );
}
